import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Alert, Badge, Card, Field, Select, Spinner } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { type DirectoryUser, isHr } from './data';
import { PeopleNav } from './PeopleNav';
import { ResetActions } from './ResetActions';

const STATUS_TONES = { activo: 'blue', inactivo: 'amber', baja: 'slate' } as const;

/** Directorio de usuarios (usuarios.ver): con las acciones del portal de RR. HH. en cada persona. */
export function DirectoryPage() {
  const me = useMe();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('activo');
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const query = new URLSearchParams();
  if (debounced) query.set('buscar', debounced);
  if (status) query.set('estado', status);
  const users = useQuery({
    queryKey: ['usuarios', 'directorio', debounced, status],
    queryFn: () => api<DirectoryUser[]>(`/api/usuarios?${query}`),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-tinta">{t('us.directorio')}</h1>
        <p className="max-w-2xl text-sm text-slate-600">{t('us.directorio_descripcion')}</p>
      </div>
      <PeopleNav />
      <div className="flex flex-wrap gap-3">
        <div className="w-72">
          <Field id="us-buscar" type="search" label={t('us.buscar')} placeholder={t('us.buscar_ayuda')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-48">
          <Select id="us-estado-usuario" label={t('us.estado')} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t('us.estado_todos')}</option>
            {(['activo', 'inactivo', 'baja'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`us.usuario_${s}` as MessageKey)}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <Card className="p-0">
        {!users.data ? (
          <div className="p-5">{users.error ? <Alert>{errorMessage(users.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>
        ) : users.data.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">{t('us.directorio_vacio')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('us.col_persona')}</th>
                  <th className="px-4 py-2 font-medium">{t('us.col_puesto')}</th>
                  <th className="px-4 py-2 font-medium">{t('us.campo_jefe')}</th>
                  <th className="px-4 py-2 font-medium">{t('us.campo_licencias')}</th>
                  <th className="px-4 py-2 font-medium">{t('us.col_acciones')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.data.map((u) => (
                  <tr key={u.id} className="align-top hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/usuarios/personas/${u.id}`} className="font-medium text-marca hover:underline">
                        {u.fullName}
                      </Link>
                      {u.status !== 'activo' && (
                        <span className="ml-1.5">
                          <Badge tone={STATUS_TONES[u.status]}>{t(`us.usuario_${u.status}` as MessageKey)}</Badge>
                        </span>
                      )}
                      <span className="block text-xs text-slate-500">
                        {u.email} · {u.organization}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-700">
                      {u.position ?? '—'}
                      {u.department && <span className="block text-xs text-slate-500">{u.department}</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{u.manager ?? '—'}</td>
                    <td className="px-4 py-2 tabular-nums text-slate-700">{u.licenses}</td>
                    <td className="px-4 py-2">
                      {u.status === 'activo' && isHr(me, u.organizationId) ? (
                        <div className="space-y-1">
                          <ResetActions userId={u.id} fullName={u.fullName} />
                          <Link to={`/usuarios/bajas/nueva?organizacion=${u.organizationId}&usuario=${u.id}`} className="text-sm text-red-700 hover:underline">
                            {t('us.dar_de_baja')}
                          </Link>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
